"use client"

import { usePathname } from "next/navigation"
import { CTA } from "@/components/sections/CTA"
import { type Locale } from "@/lib/i18n"

export default function TermsOfServicePage() {
  const pathname = usePathname()
  const currentLocale: Locale = pathname?.startsWith('/en/') ? 'en' : 'tr'
  
  return (
    <>
      <section className="bg-gradient-to-br from-[rgb(3,74,166)] to-[rgb(2,40,89)] text-white py-12 sm:py-16 lg:py-24">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl font-bold tracking-tight sm:text-5xl md:text-6xl mb-6">
              Kullanım Koşulları
            </h1>
            <p className="text-lg text-white/90 sm:text-xl">
              Son güncelleme: {new Date().toLocaleDateString('tr-TR', { year: 'numeric', month: 'long', day: 'numeric' })}
            </p>
          </div>
        </div>
      </section>

      <section className="pt-12 pb-12 lg:pb-16 bg-gradient-to-b from-white to-slate-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            {/* Content */}
            <div className="prose prose-slate max-w-none space-y-6 sm:space-y-8 text-sm sm:text-base">
              <section>
                <h2 className="text-xl sm:text-2xl font-semibold text-slate-900 mb-3 sm:mb-4 font-mono">1. Genel Hükümler</h2>
                <p>
                  Bu Kullanım Koşulları, LAKVERSE web sitesi ve hizmetlerinin kullanımına ilişkin kuralları belirlemektedir. 
                  Web sitemizi ziyaret ederek veya hizmetlerimizden yararlanarak, bu koşulları kabul etmiş sayılırsınız.
                </p>
                <p>
                  LAKVERSE olarak, bu koşulları önceden haber vermeksizin değiştirme hakkını saklı tutarız. 
                  Değişiklikler, web sitemizde yayınlandığı tarihten itibaren geçerli olacaktır.
                </p>
              </section>

              <section>
                <h2 className="text-xl sm:text-2xl font-semibold text-slate-900 mb-3 sm:mb-4 font-mono">2. Hizmetlerin Tanımı</h2>
                <p>
                  LAKVERSE, aşağıdaki hizmetleri sunmaktadır:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Web tasarım ve geliştirme hizmetleri</li>
                  <li>Mobil uygulama geliştirme</li>
                  <li>E-ticaret çözümleri</li>
                  <li>Yazılım geliştirme ve danışmanlık</li>
                  <li>AI destekli otomasyon sistemleri</li>
                  <li>Dijital dönüşüm danışmanlığı</li>
                </ul>
              </section>

              <section>
                <h2 className="text-xl sm:text-2xl font-semibold text-slate-900 mb-3 sm:mb-4 font-mono">3. Kullanıcı Yükümlülükleri</h2>
                <p>
                  Web sitemizi ve hizmetlerimizi kullanırken aşağıdaki kurallara uymanız gerekmektedir:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Yasalara ve düzenlemelere uygun davranmak</li>
                  <li>Başkalarının haklarını ihlal etmemek</li>
                  <li>Telif hakkı, marka veya diğer fikri mülkiyet haklarını ihlal etmemek</li>
                  <li>Zararlı yazılım, virüs veya kötü amaçlı kod yüklememek</li>
                  <li>Hizmetlerimizi kötüye kullanmamak veya sistem güvenliğini tehdit etmemek</li>
                  <li>Yanlış, yanıltıcı veya yasadışı bilgi paylaşmamak</li>
                </ul>
              </section>

              <section>
                <h2 className="text-xl sm:text-2xl font-semibold text-slate-900 mb-3 sm:mb-4 font-mono">4. Fikri Mülkiyet Hakları</h2>
                <p>
                  Web sitemizdeki tüm içerikler (metinler, görseller, logolar, tasarımlar, yazılımlar vb.) LAKVERSE&apos;e aittir 
                  ve telif hakkı, marka ve diğer fikri mülkiyet yasaları ile korunmaktadır.
                </p>
                <p>
                  İçeriklerimizi izinsiz kopyalama, çoğaltma, dağıtma veya ticari amaçlarla kullanma yasaktır. 
                  Ancak, kişisel ve ticari olmayan amaçlarla sınırlı olmak kaydıyla, içeriklerimizi paylaşabilirsiniz.
                </p>
              </section>

              <section>
                <h2 className="text-xl sm:text-2xl font-semibold text-slate-900 mb-3 sm:mb-4 font-mono">5. Hizmet Sözleşmeleri</h2>
                <p>
                  LAKVERSE ile yapılacak hizmet sözleşmeleri, ayrı bir sözleşme metni ile düzenlenir. 
                  Bu kullanım koşulları, genel hükümler olup, özel sözleşmelerde belirtilen koşullar önceliklidir.
                </p>
                <p>
                  Hizmet bedelleri, ödeme koşulları, teslimat süreleri ve diğer detaylar, 
                  her proje için ayrıca belirlenir ve müşteriye yazılı olarak bildirilir.
                </p>
              </section>

              <section>
                <h2 className="text-xl sm:text-2xl font-semibold text-slate-900 mb-3 sm:mb-4 font-mono">6. Garanti ve Sorumluluk Sınırlaması</h2>
                <p>
                  LAKVERSE, hizmetlerini mevcut teknoloji ve bilgi birikimi çerçevesinde en iyi şekilde sunmaya çalışır. 
                  Ancak, hizmetlerimizin kesintisiz, hatasız veya güvenli olacağına dair garanti vermez.
                </p>
                <p>
                  LAKVERSE, web sitesi veya hizmetlerin kullanımından kaynaklanan doğrudan, dolaylı, 
                  özel veya sonuç olarak ortaya çıkan zararlardan sorumlu tutulamaz.
                </p>
              </section>

              <section>
                <h2 className="text-xl sm:text-2xl font-semibold text-slate-900 mb-3 sm:mb-4 font-mono">7. Üçüncü Taraf Bağlantıları</h2>
                <p>
                  Web sitemizde, üçüncü taraf web sitelerine bağlantılar bulunabilir. 
                  Bu bağlantılar, yalnızca bilgilendirme amaçlıdır ve LAKVERSE, bu sitelerin içeriğinden 
                  veya gizlilik uygulamalarından sorumlu değildir.
                </p>
              </section>

              <section>
                <h2 className="text-xl sm:text-2xl font-semibold text-slate-900 mb-3 sm:mb-4 font-mono">8. Veri Güvenliği</h2>
                <p>
                  LAKVERSE, kişisel verilerinizin güvenliğini sağlamak için teknik ve idari önlemler alır. 
                  Ancak, internet üzerinden veri aktarımının tam güvenliğini garanti edemeyiz.
                </p>
                <p>
                  Kişisel verilerinizin işlenmesi hakkında detaylı bilgi için 
                  <a href="/gizlilik" className="text-[rgb(3,74,166)] hover:underline"> Gizlilik Politikamızı</a> inceleyebilirsiniz.
                </p>
              </section>

              <section>
                <h2 className="text-xl sm:text-2xl font-semibold text-slate-900 mb-3 sm:mb-4 font-mono">9. İptal ve İade</h2>
                <p>
                  Hizmet sözleşmelerinin iptali ve iade koşulları, her proje için ayrıca belirlenir ve 
                  sözleşme metninde yer alır. Genel olarak, başlanmış projeler için iade yapılmaz.
                </p>
              </section>

              <section>
                <h2 className="text-xl sm:text-2xl font-semibold text-slate-900 mb-3 sm:mb-4 font-mono">10. Değişiklikler ve Güncellemeler</h2>
                <p>
                  LAKVERSE, bu kullanım koşullarını herhangi bir zamanda değiştirme hakkını saklı tutar. 
                  Önemli değişiklikler, web sitemizde duyurulacaktır.
                </p>
                <p>
                  Değişikliklerden haberdar olmak için bu sayfayı düzenli olarak ziyaret etmenizi öneririz.
                </p>
              </section>

              <section>
                <h2 className="text-xl sm:text-2xl font-semibold text-slate-900 mb-3 sm:mb-4 font-mono">11. Uygulanacak Hukuk ve Yetki</h2>
                <p>
                  Bu kullanım koşulları, Türkiye Cumhuriyeti yasalarına tabidir. 
                  Bu koşullardan kaynaklanan uyuşmazlıklar, Türkiye Cumhuriyeti mahkemelerinde çözülecektir.
                </p>
              </section>

              <section>
                <h2 className="text-xl sm:text-2xl font-semibold text-slate-900 mb-3 sm:mb-4 font-mono">12. İletişim</h2>
                <p>
                  Kullanım koşulları ile ilgili sorularınız için bizimle iletişime geçebilirsiniz:
                </p>
                <div className="bg-slate-50 p-4 sm:p-6 rounded-lg my-4 text-sm sm:text-base">
                  <p className="font-semibold mb-2">LAKVERSE</p>
                  <p className="text-sm text-slate-600">
                    E-posta: info@lakverse.com<br />
                    Telefon: 0090 531 722 1489<br />
                    Web: www.lakverse.com
                  </p>
                </div>
              </section>
            </div>
          </div>
        </div>
      </section>
      <CTA locale={currentLocale} />
    </>
  )
}
